import { useQuery } from "@apollo/client";
import { UseAutocompleteProps, Typography, Box } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { gql } from "generated-graphql";
import * as React from "react";

const GET_FACILITY_TAGS = gql(`
  query GetFacilityTags($search: String!) {
    tags(
      search: $search
    ) {
      items {
        name
      }
    }
  }
`);

type TagOption = {
  inputValue?: string;
  tag: string;
  isCommon?: boolean;
  isRecent?: boolean;
  isSelected?: boolean;
};

const filter = createFilterOptions<TagOption>();

type FacilityTagPickerProps = {
  onAddTag: (tag: string) => void;
  disabled?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  commonTags?: string[];
  recentTags?: string[];
  selectedTags?: string[];
  tenantId: string;
};

export default function FacilityTagPicker(props: FacilityTagPickerProps) {
  const [value, setValue] = React.useState<TagOption | null>(null);
  const { data } = useQuery(GET_FACILITY_TAGS, {
    variables: { search: `type:Facility tenantId:${props.tenantId}` },
  });
  const { commonTags = [], recentTags = [], selectedTags = [] } = props;

  const selectedTagsSet = React.useMemo(() => new Set(selectedTags), [selectedTags]);

  const tags: TagOption[] = React.useMemo(() => {
    // Build all available tags
    const allTags: TagOption[] = [];

    // Add common tags first
    commonTags.forEach((tag) => {
      allTags.push({
        tag,
        isCommon: true,
        isSelected: selectedTagsSet.has(tag),
      });
    });

    // Add recent tags (but not duplicates of common tags)
    const commonTagsSet = new Set(commonTags);
    recentTags.forEach((tag) => {
      if (!commonTagsSet.has(tag)) {
        allTags.push({
          tag,
          isRecent: true,
          isSelected: selectedTagsSet.has(tag),
        });
      }
    });

    // Add all other tags from the database
    const usedTags = new Set([...commonTags, ...recentTags]);
    (data?.tags?.items ?? []).forEach((tag: { name: string }) => {
      if (!usedTags.has(tag.name)) {
        allTags.push({ tag: tag.name });
      }
    });

    return allTags;
  }, [data?.tags?.items, commonTags, recentTags, selectedTagsSet]);

  const onChange = React.useCallback(
    (event: React.SyntheticEvent<Element, Event>, newTag: string | TagOption | null) => {
      // Ignore header selections
      if (newTag && typeof newTag === "object" && newTag.inputValue === "header") {
        return;
      }

      if (typeof newTag === "string") {
        setValue({
          tag: newTag,
        });
        props.onAddTag(newTag);
      } else if (newTag && newTag.inputValue) {
        // Create a new value from the user input
        setValue({
          tag: newTag.inputValue,
        });
        props.onAddTag(newTag.inputValue);
      } else {
        setValue(newTag);
        if (newTag) {
          props.onAddTag(newTag.tag);
        }
      }
    },
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
    [props.onAddTag]
  );

  const filterOptions: UseAutocompleteProps<TagOption, false, false, true>["filterOptions"] = (
    options,
    params
  ) => {
    const filtered = filter(options, params);
    const { inputValue } = params;

    // Group filtered options
    const commonOptions = filtered.filter((opt) => opt.isCommon && !opt.isSelected);
    const recentOptions = filtered.filter((opt) => opt.isRecent && !opt.isSelected);
    const otherOptions = filtered.filter((opt) => !opt.isCommon && !opt.isRecent);

    const result: TagOption[] = [];

    // Add commonly used section
    if (commonOptions.length > 0) {
      result.push({ tag: "Commonly Used Tags", inputValue: "header" });
      result.push(...commonOptions);
    }

    // Add recent section
    if (recentOptions.length > 0) {
      result.push({ tag: "Recent Tags", inputValue: "header" });
      result.push(...recentOptions);
    }

    // Add other options
    if (otherOptions.length > 0 && (commonOptions.length > 0 || recentOptions.length > 0)) {
      result.push({ tag: "All Tags", inputValue: "header" });
    }
    result.push(...otherOptions);

    // Suggest the creation of a new value
    const isExisting = options.some((option) => inputValue === option.tag);
    if (inputValue !== "" && !isExisting) {
      result.push({
        inputValue,
        tag: `Add "${inputValue}"`,
      });
    }

    return result;
  };

  const getOptionLabel = React.useCallback((option: string | TagOption) => {
    // Value selected with enter, right from the input
    if (typeof option === "string") {
      return option;
    }
    // Add "xxx" option created dynamically
    if (option.inputValue) {
      return option.inputValue;
    }
    // Regular option
    return option.tag;
  }, []);

  return (
    <Autocomplete
      freeSolo
      value={value}
      onChange={onChange}
      filterOptions={filterOptions}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      disabled={props.disabled}
      id="facility-tag-picker"
      sx={{ width: "100%" }}
      options={tags}
      getOptionLabel={getOptionLabel}
      renderOption={(props, option) => {
        // Render section headers
        if (option.inputValue === "header") {
          return (
            <Box key={option.tag} sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                {option.tag}
              </Typography>
            </Box>
          );
        }

        // Render regular options with indentation
        return (
          <li
            {...props}
            key={option.tag}
            style={{
              ...props.style,
              opacity: option.isSelected ? 0.5 : 1,
              pointerEvents: option.isSelected ? "none" : "auto",
              paddingLeft: "32px", // Add indentation for tag items
            }}
          >
            {option.tag}
          </li>
        );
      }}
      renderInput={(params) => <TextField {...params} label="Add tag" inputRef={props.inputRef} />}
    />
  );
}
